<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { api, handleApiError } from '../../lib/api'
  import { fade, slide } from 'svelte/transition'
  import { addToast } from '../../lib/toast'
  import ConfirmModal from '../../components/ui/ConfirmModal.svelte'
  import QRCode from 'qrcode'

  // Helper function to get session type name
  function getSessionTypeName(type?: string): string {
    switch (type) {
      case 'telegram_mtproto': return 'Telegram MTProto'
      case 'telegram_bot': return 'Telegram Bot'
      case 'email': return 'Email'
      case 'whatsapp':
      default: return 'WhatsApp'
    }
  }
  
  // Helper function to check if session supports QR
  function supportsQR(type?: string): boolean {
    return type === 'whatsapp' || !type
  }

  function formatRestrictedUntil(until?: string | null): string {
    if (!until) return ''
    const date = new Date(until)
    if (Number.isNaN(date.getTime())) return until
    return date.toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  type AdminSession = { 
    id: string; 
    user_id: string; 
    label: string; 
    number: string; 
    type?: string;
    active: boolean;
    connected?: boolean;
    restricted?: boolean;
    restricted_until?: string | null;
    created_at?: string;
    user_email?: string;
    user_name?: string;
  }

  type ClientUser = {
    id: string;
    email: string;
    name: string;
  }

  let sessions: AdminSession[] = []
  let clientUsers: ClientUser[] = []
  let loading = false
  let error = ''
  
  // Paginación y filtro
  let currentPage = 1
  let pageSize = 10
  let totalPages = 1
  let total = 0
  let searchQuery = ''
  let searchTimeout: ReturnType<typeof setTimeout>
  
  // Modal State
  let showModal = false
  let modalMode: 'create' | 'edit' = 'create'
  let editingId = ''
  
  // Confirm Modal State
  let showConfirm = false
  let confirmId = ''
  let confirmAction: 'disconnect' | 'logout' | 'delete' | null = null

  // QR Modal State
  let showQRModal = false
  let qrSessionId = ''
  let qrLoading = false
  let qrImageUrl = ''
  let qrPublicUrl = ''
  
  // Helper reactive statement to get QR session
  $: qrSession = sessions.find(s => s.id === qrSessionId)

  // API Info Modal State
  let showAPIInfoModal = false
  let apiInfoSessionId = ''
  let apiInfoLoading = false
  let apiInfo: { appkey: string; authkey: string } | null = null
  
  // API QR State
  let apiQRImage = ''
  let apiQRTempUrl = ''
  let generatingTempUrl = false
  let qrToken = ''
  let qrStatus: {
    active: boolean
    expired: boolean
    requests_used: number
    requests_remaining: number
    max_requests: number
    seconds_remaining: number
    expires_in_minutes: number
  } | null = null
  let checkingStatus = false
  let statusCheckInterval: ReturnType<typeof setInterval> | null = null

  // Form Data
  let formData = {
    user_id: '',
    label: '',
    number: '',
    type: 'whatsapp' as 'whatsapp' | 'telegram_mtproto' | 'telegram_bot' | 'email'
  }
  let formLoading = false
  
  // Configurations (conditional based on type)
  let telegramConfig = { api_id: '', api_hash: '', phone_number: '', password: '', bot_token: '' }
  let emailConfig = { smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', from_email: '', from_name: '', tls: true }

  async function loadSessions() {
    loading = true
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      })
      if (searchQuery) {
        params.append('search', searchQuery)
      }
      
      const res = await api.get(`/api/v1/admin/sessions?${params.toString()}`)
      sessions = res.sessions || []
      total = res.pagination?.total || 0
      totalPages = res.pagination?.totalPages || 1
    } catch (e: any) {
      error = e.message
      addToast('Error al cargar sesiones: ' + e.message, 'error')
      sessions = []
    } finally {
      loading = false
    }
  }
  
  function handleSearch() {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      currentPage = 1
      loadSessions()
    }, 500) // Debounce de 500ms
  }
  
  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages) {
      currentPage = page
      loadSessions()
    }
  }

  async function loadClientUsers() {
    try {
      const res = await api.get('/api/v1/admin/users/clients')
      clientUsers = res.users || []
    } catch (e: any) {
      console.error('Error cargando usuarios client:', e)
      clientUsers = []
    }
  }

  async function showQR(sessionId: string) {
    qrSessionId = sessionId
    qrLoading = true
    showQRModal = true
    qrImageUrl = ''
    qrPublicUrl = `http://localhost:8060/api/v1/public/qr/${sessionId}`
    
    try {
      // Polling para verificar cuando el QR esté disponible
      const maxAttempts = 30 // 30 intentos
      const pollInterval = 1000 // 1 segundo entre intentos
      let attempts = 0
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval))
        attempts++
        
        // Agregar timestamp para evitar caché
        const timestamp = Date.now()
        const testUrl = `${qrPublicUrl}?t=${timestamp}`
        
        try {
          const response = await fetch(testUrl)
          const contentType = response.headers.get('content-type')
          
          if (response.status === 200 && contentType?.includes('image/png')) {
            // QR está disponible
            qrImageUrl = qrPublicUrl
            qrLoading = false
            addToast('QR generado. Puedes compartir la URL pública.', 'success')
            return
          } else if (response.status === 202) {
            // QR pendiente (202 Accepted) - continuar esperando
            console.log(`Intento ${attempts}: QR aún no disponible (202 Accepted)`)
          } else {
            console.log(`Intento ${attempts}: Estado inesperado ${response.status}`)
          }
        } catch (e) {
          // Continuar intentando
          console.log(`Intento ${attempts}: Error verificando QR:`, e)
        }
      }
      
      // Si llegamos aquí, el QR no se generó a tiempo
      addToast('El QR no se generó a tiempo. Intenta nuevamente.', 'error')
      qrImageUrl = ''
    } catch (e: any) {
      addToast('Error al generar QR: ' + e.message, 'error')
      qrImageUrl = ''
    } finally {
      qrLoading = false
    }
  }

  function copyQRUrl() {
    if (qrPublicUrl) {
      navigator.clipboard.writeText(qrPublicUrl)
      addToast('URL copiada al portapapeles', 'success')
    }
  }

  async function showAPIInfo(sessionId: string) {
    apiInfoSessionId = sessionId
    apiInfoLoading = true
    showAPIInfoModal = true
    apiInfo = null
    apiQRImage = ''
    apiQRTempUrl = ''
    qrToken = ''
    qrStatus = null
    
    // Limpiar intervalo anterior si existe
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval)
      statusCheckInterval = null
    }
    
    try {
      const res = await api.get(`/api/v1/admin/sessions/${sessionId}/api-info`)
      apiInfo = {
        appkey: res.appkey,
        authkey: res.authkey
      }
      // NO generar QR automáticamente - el usuario debe hacerlo manualmente
    } catch (e: any) {
      addToast('Error al obtener información de API: ' + e.message, 'error')
      showAPIInfoModal = false
    } finally {
      apiInfoLoading = false
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text)
    addToast(`${label} copiado al portapapeles`, 'success')
  }

  // // Función para obtener la URL base según el entorno
  // function getBaseUrl(): string {
  //   if (import.meta.env.DEV) {
  //     return 'http://localhost:8060'
  //   }
  //   // En producción, usar el origin actual
  //   return window.location.origin
  // }

  // Generar QR con la URL temporal que devuelve los datos de API
  async function generateAPIQR() {
    if (!apiInfo || !apiInfoSessionId) return
    
    generatingTempUrl = true
    try {
      // Generar la URL temporal
      const res = await api.post(`/api/v1/admin/sessions/${apiInfoSessionId}/api-qr-temp`)
      apiQRTempUrl = res.url
      qrToken = res.token
      
      // Generar QR con solo la URL pública (más seguro)
      apiQRImage = await QRCode.toDataURL(apiQRTempUrl, {
        errorCorrectionLevel: 'M',
        type: 'image/png',
        width: 400,
        margin: 2
      })
      
      // Iniciar consulta de estado
      await checkQRStatus()
      startStatusPolling()
      
      addToast('QR generado exitosamente. Expira en 5 requests o 5 minutos.', 'success')
    } catch (e: any) {
      console.error('Error generando QR:', e)
      addToast('Error al generar QR: ' + e.message, 'error')
    } finally {
      generatingTempUrl = false
    }
  }
  
  // Consultar estado del QR
  async function checkQRStatus() {
    if (!apiInfoSessionId || !qrToken) return
    
    checkingStatus = true
    try {
      const res = await api.get(`/api/v1/admin/sessions/${apiInfoSessionId}/api-qr-status/${qrToken}`)
      qrStatus = res
    } catch (e: any) {
      // Si falla, asumir que expiró
      qrStatus = {
        active: false,
        expired: true,
        requests_used: 5,
        requests_remaining: 0,
        max_requests: 5,
        seconds_remaining: 0,
        expires_in_minutes: 0
      }
    } finally {
      checkingStatus = false
    }
  }
  
  // Iniciar polling del estado cada 10 segundos
  function startStatusPolling() {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval)
    }
    
    statusCheckInterval = setInterval(async () => {
      if (qrToken && apiInfoSessionId) {
        await checkQRStatus()
        // Si expiró, detener el polling
        if (qrStatus?.expired) {
          if (statusCheckInterval) {
            clearInterval(statusCheckInterval)
            statusCheckInterval = null
          }
        }
      }
    }, 10000) // Cada 10 segundos
  }
  
  // Detener polling del estado
  function stopStatusPolling() {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval)
      statusCheckInterval = null
    }
  }

  // Descargar QR como PNG
  function downloadQR() {
    if (!apiQRImage) return
    
    try {
      const link = document.createElement('a')
      link.href = apiQRImage
      link.download = `api-qr-${apiInfoSessionId.slice(0, 8)}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      addToast('QR descargado correctamente', 'success')
    } catch (e: any) {
      addToast('Error al descargar QR: ' + e.message, 'error')
    }
  }

  // Regenerar URL y QR (revoca la anterior)
  async function regenerateQR() {
    // Limpiar estado anterior
    stopStatusPolling()
    apiQRImage = ''
    apiQRTempUrl = ''
    qrToken = ''
    qrStatus = null
    
    // Generar nuevo QR
    await generateAPIQR()
  }

  // Copiar URL temporal al portapapeles
  function copyTempQRUrl() {
    if (apiQRTempUrl) {
      navigator.clipboard.writeText(apiQRTempUrl)
      addToast('URL temporal copiada al portapapeles', 'success')
    }
  }

  function openCreateModal() {
    modalMode = 'create'
    formData = { user_id: '', label: '', number: '', type: 'whatsapp' }
    telegramConfig = { api_id: '', api_hash: '', phone_number: '', password: '', bot_token: '' }
    emailConfig = { smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', from_email: '', from_name: '', tls: true }
    showModal = true
  }

  function openEditModal(s: AdminSession) {
    modalMode = 'edit'
    editingId = s.id
    formData = { 
      user_id: s.user_id, // Note: Edit endpoint might not allow changing user_id, but we show it
      label: s.label, 
      number: s.number,
      type: (s as any).type || 'whatsapp' // Support type if available
    }
    showModal = true
  }

  async function handleSubmit() {
    formLoading = true
    try {
      const payload: any = {
        user_id: formData.user_id,
        label: formData.label,
        number: formData.number,
        type: formData.type
      }
      
      // Add configuration based on type
      if (formData.type === 'telegram_mtproto' || formData.type === 'telegram_bot') {
        if (formData.type === 'telegram_mtproto') {
          payload.telegram_config = {
            api_id: parseInt(telegramConfig.api_id) || 0,
            api_hash: telegramConfig.api_hash,
            phone_number: telegramConfig.phone_number || formData.number,
            password: telegramConfig.password || ''
          }
        } else {
          payload.telegram_config = {
            bot_token: telegramConfig.bot_token
          }
        }
      } else if (formData.type === 'email') {
        payload.email_config = {
          smtp_host: emailConfig.smtp_host,
          smtp_port: parseInt(emailConfig.smtp_port.toString()) || 587,
          smtp_user: emailConfig.smtp_user,
          smtp_password: emailConfig.smtp_password,
          from_email: emailConfig.from_email || emailConfig.smtp_user,
          from_name: emailConfig.from_name || '',
          tls: emailConfig.tls
        }
      }
      
      if (modalMode === 'create') {
        const response = await api.post('/api/v1/admin/sessions', payload)
        addToast(response.message || 'Sesión creada exitosamente', 'success')
      } else {
        const response = await api.put(`/api/v1/admin/sessions/${editingId}`, payload)
        addToast(response.message || 'Sesión actualizada exitosamente', 'success')
      }
      showModal = false
      currentPage = 1
      loadSessions()
    } catch (e: any) {
      addToast(e.message, 'error')
    } finally {
      formLoading = false
    }
  }

  async function toggleActive(s: AdminSession) {
    try {
      await api.put(`/api/v1/admin/sessions/${s.id}/active`, { active: !s.active })
      // Optimistic update
      s.active = !s.active
      sessions = [...sessions]
      addToast(`Sesión ${s.active ? 'activada' : 'desactivada'} correctamente`, 'success')
    } catch (e: any) {
      addToast(e.message, 'error')
      loadSessions() // Revert on error
    }
  }

  function confirmDelete(id: string) {
    console.log('confirmDelete llamado con id:', id)
    confirmId = id
    confirmAction = 'delete'
    showConfirm = true
    console.log('showConfirm establecido a:', showConfirm, 'confirmAction:', confirmAction)
  }
  
  function openDisconnectConfirm(id: string) {
    confirmId = id
    confirmAction = 'disconnect'
    showConfirm = true
  }
  
  function openLogoutConfirm(id: string) {
    confirmId = id
    confirmAction = 'logout'
    showConfirm = true
  }

  async function deleteSession() {
    console.log('deleteSession iniciado para sessionId:', confirmId)
    if (!confirmId) {
      console.error('confirmId está vacío')
      addToast('Error: ID de sesión no válido', 'error')
      return
    }
    try {
      console.log('Enviando DELETE a:', `/api/v1/admin/sessions/${confirmId}`)
      const response = await api.delete(`/api/v1/admin/sessions/${confirmId}`)
      console.log('Respuesta del servidor:', response)
      // Eliminar de la lista local
      sessions = sessions.filter(s => s.id !== confirmId)
      total = Math.max(0, total - 1)
      showConfirm = false
      confirmAction = null
      // Manejar diferentes formatos de respuesta
      const message = response?.message || response?.data?.message || 'Sesión eliminada correctamente'
      addToast(message, 'success')
      // Recargar sesiones para asegurar sincronización
      await loadSessions()
    } catch (e: any) {
      console.error('Error en deleteSession:', e)
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
      // Cerrar el modal incluso si hay error
      showConfirm = false
      confirmAction = null
    }
  }
  
  async function disconnectSession() {
    try {
      await api.post(`/api/v1/admin/sessions/${encodeURIComponent(confirmId)}/disconnect`)
      currentPage = 1
      await loadSessions()
      showConfirm = false
      confirmAction = null
      addToast('Sesión desconectada correctamente', 'success')
    } catch (e: any) {
      addToast('Error al desconectar: ' + e.message, 'error')
    }
  }
  
  async function logoutSession() {
    try {
      await api.post(`/api/v1/admin/sessions/${encodeURIComponent(confirmId)}/logout`)
      currentPage = 1
      await loadSessions()
      showConfirm = false
      confirmAction = null
      addToast('Sesión desvinculada. Deberás escanear el QR nuevamente para reconectar.', 'success')
    } catch (e: any) {
      addToast('Error al desvincular: ' + e.message, 'error')
    }
  }
  
  function getConfirmTitle(): string {
    switch (confirmAction) {
      case 'disconnect': return 'Desconectar Sesión'
      case 'logout': return 'Desvincular Sesión'
      case 'delete': return 'Eliminar Sesión'
      default: return 'Confirmar Acción'
    }
  }
  
  function getConfirmMessage(): string {
    switch (confirmAction) {
      case 'disconnect': return '¿Estás seguro de que deseas desconectar esta sesión? La sesión se mantendrá pero se cerrará la conexión.'
      case 'logout': return '¿Estás seguro de que deseas desvincular esta sesión? Deberás escanear el QR nuevamente para reconectar.'
      case 'delete': return '¿Estás seguro de que deseas eliminar esta sesión? Esta acción no se puede deshacer y se perderán todos los datos asociados.'
      default: return '¿Estás seguro?'
    }
  }
  
  async function handleConfirm() {
    console.log('handleConfirm llamado, confirmAction:', confirmAction, 'confirmId:', confirmId)
    switch (confirmAction) {
      case 'disconnect': 
        await disconnectSession()
        break
      case 'logout': 
        await logoutSession()
        break
      case 'delete': 
        console.log('Ejecutando deleteSession para:', confirmId)
        await deleteSession()
        break
      default:
        console.warn('confirmAction desconocido:', confirmAction)
    }
  }

  onMount(() => {
    loadSessions()
    loadClientUsers()
  })
  
  onDestroy(() => {
    stopStatusPolling()
  })
</script>

<div class="space-y-8">
  <div class="flex flex-col md:flex-row md:items-end justify-between gap-4">
    <div>
      <h2 class="text-4xl font-black text-white tracking-tight mb-2">Administración de Sesiones</h2>
      <p class="text-indigo-200/60 font-medium">Gestiona todas las sesiones del sistema</p>
    </div>
    <button 
      on:click={openCreateModal}
      class="group px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
    >
      <div class="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 -skew-x-12 -translate-x-full"></div>
      <span class="relative z-10 flex items-center"><span class="mr-2 text-xl leading-none">+</span> Nueva Sesión</span>
    </button>
  </div>

  <!-- Filtro de búsqueda -->
  <div class="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-sm">
    <div class="flex gap-4 items-end">
      <div class="flex-1">
        <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Buscar Sesiones</label>
        <div class="relative">
          <input 
            type="text" 
            bind:value={searchQuery}
            on:input={handleSearch}
            placeholder="Buscar por ID, etiqueta o número..."
            class="w-full px-4 py-3 pl-12 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
          />
          <div class="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-200/40">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Sessions Table -->
  <div class="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm relative z-10">
    <div class="overflow-x-auto">
      <table class="w-full text-left border-collapse">
        <thead>
          <tr class="border-b border-white/10 bg-black/20">
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">ID / Usuario</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">Etiqueta</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">Número</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">Estado</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider text-right">Acciones</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider text-right">QR</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-white/5">
          {#if loading}
            <tr>
              <td colspan="6" class="p-8 text-center text-indigo-200/50 animate-pulse">Cargando sesiones...</td>
            </tr>
          {:else if sessions.length === 0}
            <tr>
              <td colspan="6" class="p-8 text-center text-indigo-200/50">No hay sesiones registradas</td>
            </tr>
          {:else}
            {#each sessions as s}
              <tr class="hover:bg-white/5 transition-colors group">
                <td class="p-5">
                  <div class="font-mono text-xs text-white/70">{s.id.slice(0, 8)}...</div>
                  {#if s.user_email || s.user_name}
                    <div class="text-xs text-indigo-300/70 mt-1">
                      {s.user_name || s.user_email || s.user_id}
                    </div>
                    {#if s.user_email && s.user_name}
                      <div class="text-xs text-indigo-300/40 mt-0.5">{s.user_email}</div>
                    {/if}
                  {:else}
                    <div class="text-xs text-indigo-300/50 mt-1">User: {s.user_id}</div>
                  {/if}
                </td>
                <td class="p-5 font-medium text-white">{s.label}</td>
                <td class="p-5">
                  <div class="text-indigo-200/70">{s.number || '-'}</div>
                  {#if s.type && s.type !== 'whatsapp'}
                    <div class="text-xs text-indigo-300/50 mt-1">{getSessionTypeName(s.type)}</div>
                  {/if}
                </td>
                <td class="p-5">
                  <div class="flex flex-col gap-2">
                    <button 
                      on:click={() => toggleActive(s)}
                      class="px-3 py-1 rounded-full text-xs font-bold border transition-all {s.active ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30'}"
                    >
                      {s.active ? 'ACTIVO' : 'INACTIVO'}
                    </button>
                    {#if s.restricted}
                      <span
                        class="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full text-center flex items-center justify-center gap-1"
                        title="WhatsApp limitó envíos a contactos nuevos (error 463)"
                      >
                        <span class="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                        Restringido
                        {#if s.restricted_until}
                          · hasta {formatRestrictedUntil(s.restricted_until)}
                        {/if}
                      </span>
                    {:else if s.connected}
                      <span class="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full text-center flex items-center justify-center gap-1">
                        <span class="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                        Conectado
                      </span>
                    {:else}
                      <span class="text-xs bg-gray-500/20 text-gray-300 px-2 py-0.5 rounded-full text-center">Desconectado</span>
                    {/if}
                  </div>
                </td>
                <td class="p-5 text-right">
                  <div class="flex items-center justify-end gap-2 flex-wrap">
                    {#if s.connected}
                      <button 
                        on:click={() => openDisconnectConfirm(s.id)}
                        class="px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 transition-colors"
                        title="Parar"
                      >
                        ⏸️ Parar
                      </button>
                      <button 
                        on:click={() => openLogoutConfirm(s.id)}
                        class="px-3 py-1.5 rounded-lg text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 transition-colors"
                        title="Desvincular"
                      >
                        🔓 Desvincular
                      </button>
                    {/if}
                    <button 
                      on:click={() => showAPIInfo(s.id)}
                      class="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                      title="Ver API Info"
                    >
                      🔑 API
                    </button>
                    <button 
                      on:click={() => openEditModal(s)}
                      class="p-2 rounded-lg text-indigo-200/60 hover:text-white hover:bg-white/10 transition-colors"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button 
                      on:click={() => confirmDelete(s.id)}
                      class="p-2 rounded-lg text-red-400/60 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
                <td class="p-5 text-right">
                  {#if s.restricted}
                    <span class="text-xs text-red-300">Restringido</span>
                  {:else if s.connected}
                    <span class="text-xs text-indigo-200/40">Conectado</span>
                  {:else}
                    <button 
                      on:click={() => showQR(s.id)}
                      class="px-4 py-2 rounded-lg text-xs font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                      title="Ver QR"
                    >
                      📱 QR
                    </button>
                  {/if}
                </td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </table>
    </div>
    
    <!-- Paginación -->
    {#if totalPages > 1}
      <div class="p-6 border-t border-white/10 flex items-center justify-between">
        <div class="text-sm text-indigo-200/60">
          Mostrando {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, total)} de {total} sesiones
        </div>
        <div class="flex gap-2">
          <button
            on:click={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            class="px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            Anterior
          </button>
          
          {#each Array(Math.min(5, totalPages)) as _, i}
            {@const page = currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i}
            {#if page >= 1 && page <= totalPages}
              <button
                on:click={() => goToPage(page)}
                class="px-4 py-2 rounded-lg border transition-colors {currentPage === page ? 'bg-pink-500/20 text-pink-300 border-pink-500/30' : 'bg-black/30 border-white/10 text-white hover:bg-white/10'}"
              >
                {page}
              </button>
            {/if}
          {/each}
          
          <button
            on:click={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            class="px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            Siguiente
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<!-- Modal -->
{#if showModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" transition:fade>
    <div class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden" transition:slide>
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500"></div>
      
      <h3 class="text-2xl font-black text-white mb-6">
        {modalMode === 'create' ? 'Nueva Sesión Admin' : 'Editar Sesión'}
      </h3>
      
      <div class="space-y-5">
        {#if modalMode === 'create'}
          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Usuario</label>
            <select 
              bind:value={formData.user_id}
              class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none"
            >
              <option value="">Seleccionar usuario...</option>
              {#each clientUsers as user}
                <option value={user.id}>{user.name || user.email} ({user.email})</option>
              {/each}
            </select>
          </div>
        {/if}
        
        <div>
          <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Etiqueta (Label)</label>
          <input 
            bind:value={formData.label}
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
            placeholder="Ej: Marketing"
          />
        </div>
        
        <div>
          <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Tipo de Sesión</label>
          <select 
            bind:value={formData.type}
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none"
          >
            <option value="whatsapp">WhatsApp</option>
            <!-- <option value="telegram_mtproto">Telegram MTProto</option> -->
            <option value="telegram_bot">Telegram Bot</option>
            <option value="email">Email (SMTP)</option>
          </select>
        </div>
        
        <div>
          <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
            {formData.type === 'email' ? 'Email / Identificador' : 'Número'}
          </label>
          <input 
            bind:value={formData.number}
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
            placeholder={formData.type === 'email' ? 'ejemplo@email.com' : '57300...'}
          />
        </div>
        
        <!-- Telegram MTProto Configuration -->
        {#if formData.type === 'telegram_mtproto'}
          <div class="space-y-3 pt-3 border-t border-white/10">
            <p class="text-xs font-bold text-indigo-200 uppercase tracking-wider">Configuración Telegram MTProto</p>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">API ID</label>
              <input 
                bind:value={telegramConfig.api_id}
                type="number"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="12345678"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">API Hash</label>
              <input 
                bind:value={telegramConfig.api_hash}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="abcdef1234567890abcdef1234567890"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">Número de Teléfono</label>
              <input 
                bind:value={telegramConfig.phone_number}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="+1234567890"
              />
              <p class="text-xs text-indigo-300/50 mt-1">
                Requerido para autenticación. Formato: +1234567890
              </p>
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">Contraseña 2FA (Opcional)</label>
              <input 
                bind:value={telegramConfig.password}
                type="password"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="Solo si tienes 2FA activado"
              />
              <p class="text-xs text-indigo-300/50 mt-1">
                Solo si tu cuenta tiene autenticación de dos factores activada
              </p>
            </div>
          </div>
        {/if}
        
        <!-- Telegram Bot Configuration -->
        {#if formData.type === 'telegram_bot'}
          <div class="space-y-3 pt-3 border-t border-white/10">
            <p class="text-xs font-bold text-indigo-200 uppercase tracking-wider">Configuración Telegram Bot</p>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">Bot Token</label>
              <input 
                bind:value={telegramConfig.bot_token}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              />
            </div>
          </div>
        {/if}
        
        <!-- Email Configuration -->
        {#if formData.type === 'email'}
          <div class="space-y-3 pt-3 border-t border-white/10">
            <p class="text-xs font-bold text-indigo-200 uppercase tracking-wider">Configuración SMTP</p>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">SMTP Host</label>
              <input 
                bind:value={emailConfig.smtp_host}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">SMTP Port</label>
              <input 
                bind:value={emailConfig.smtp_port}
                type="number"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="587"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">SMTP User</label>
              <input 
                bind:value={emailConfig.smtp_user}
                type="email"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="mi@email.com"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">SMTP Password</label>
              <input 
                bind:value={emailConfig.smtp_password}
                type="password"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">From Email (Opcional)</label>
              <input 
                bind:value={emailConfig.from_email}
                type="email"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="remitente@email.com"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1">From Name (Opcional)</label>
              <input 
                bind:value={emailConfig.from_name}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="Mi Nombre"
              />
            </div>
            <div class="flex items-center gap-2">
              <input 
                type="checkbox" 
                bind:checked={emailConfig.tls}
                id="tls-check-admin"
                class="w-4 h-4 rounded bg-black/30 border border-white/10 text-pink-500 focus:ring-pink-500/50"
              />
              <label for="tls-check-admin" class="text-xs text-indigo-300/70">Usar TLS</label>
            </div>
          </div>
        {/if}
      </div>
      
      <div class="flex justify-end gap-3 mt-10">
        <button 
          on:click={() => showModal = false}
          class="px-6 py-3 bg-gray-700/50 hover:bg-gray-700 text-white rounded-xl font-bold transition-colors"
        >
          Cancelar
        </button>
        <button 
          on:click={handleSubmit}
          disabled={formLoading}
          class="px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {formLoading ? 'Guardando...' : (modalMode === 'create' ? 'Crear' : 'Guardar')}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Confirm Modal -->
<ConfirmModal 
  isOpen={showConfirm}
  title={getConfirmTitle()}
  message={getConfirmMessage()}
  confirmText="Confirmar"
  cancelText="Cancelar"
  type="danger"
  on:confirm={handleConfirm}
  on:cancel={() => { showConfirm = false; confirmAction = null; }}
/>

<!-- QR Modal -->
{#if showQRModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" transition:fade>
    <div class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden" transition:slide>
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500"></div>
      
      <h3 class="text-2xl font-black text-white mb-6">Código QR de Sesión</h3>
      
      <div class="space-y-6">
        {#if qrLoading}
          <div class="text-center py-12">
            <p class="text-indigo-200/60 animate-pulse">Generando QR...</p>
          </div>
        {:else if qrImageUrl}
          <div class="bg-white p-4 rounded-xl">
            <img 
              src={`${qrImageUrl}?t=${Date.now()}`} 
              alt="QR Code" 
              class="w-64 h-64"
              on:error={(e) => {
                console.error('Error cargando QR:', e)
                // No mostrar toast aquí, el polling ya maneja los errores
              }}
              on:load={() => {
                console.log('QR cargado exitosamente')
              }}
            />
          </div>
          <p class="text-indigo-300/90 text-base text-center font-medium mt-2">
            {#if qrSession && supportsQR(qrSession.type)}
              Escanea este código con WhatsApp para vincular la sesión
            {:else}
              Escanea este código para vincular la sesión
            {/if}
          </p>
          
          <!-- URL pública -->
          <div class="w-full space-y-2">
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider">URL Pública del QR</label>
            <div class="flex gap-2">
              <input 
                type="text" 
                value={qrPublicUrl}
                readonly
                class="flex-1 px-4 py-2 rounded-xl bg-black/30 border border-white/10 text-white text-sm font-mono"
              />
              <button 
                on:click={copyQRUrl}
                class="px-4 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 rounded-xl transition-colors font-medium"
                title="Copiar URL"
              >
                📋
              </button>
            </div>
            <p class="text-sm text-indigo-300/80 mt-2 font-medium">
              Comparte esta URL para que otros puedan escanear el QR
            </p>
          </div>
        {:else}
          <div class="text-center py-12">
            <p class="text-indigo-200/60">No se pudo generar el QR. Intenta nuevamente.</p>
          </div>
        {/if}
      </div>

      <div class="flex justify-end gap-3 mt-10">
        <button 
          on:click={() => { showQRModal = false; qrImageUrl = ''; qrPublicUrl = ''; }}
          class="px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- API Info Modal -->
{#if showAPIInfoModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" transition:fade>
    <div class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] p-6 relative overflow-hidden flex flex-col" transition:slide>
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>
      
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-black text-white">
          Información de API
        </h3>
        <button 
          on:click={() => {
            showAPIInfoModal = false
            apiInfo = null
            apiQRImage = ''
            apiQRTempUrl = ''
            qrToken = ''
            qrStatus = null
            stopStatusPolling()
          }}
          class="px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
        >
          ✕ Cerrar
        </button>
      </div>
      
      {#if apiInfoLoading}
        <div class="text-center py-12">
          <p class="text-indigo-200/60 animate-pulse">Cargando información...</p>
        </div>
      {:else if apiInfo}
        <div class="flex gap-6 flex-1 overflow-hidden">
          <!-- Columna izquierda: Campos y ejemplo -->
          <div class="flex-1 space-y-4 overflow-y-auto pr-2">
            <p class="text-xs text-indigo-300/70 font-medium">
              Usa estos valores para enviar mensajes desde aplicaciones externas:
            </p>
            
            <!-- AppKey (Session ID) -->
            <div class="space-y-1.5">
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider">AppKey (Session ID)</label>
              <div class="flex gap-2">
                <input 
                  type="text" 
                  value={apiInfo.appkey}
                  readonly
                  class="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                />
                <button 
                  on:click={() => apiInfo && copyToClipboard(apiInfo.appkey, 'AppKey')}
                  class="px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors text-xs font-medium"
                  title="Copiar AppKey"
                >
                  📋
                </button>
              </div>
              <p class="text-xs text-indigo-300/60">
                ID de la sesión
              </p>
            </div>
            
            <!-- AuthKey (API Token) -->
            <div class="space-y-1.5">
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider">AuthKey (API Token)</label>
              <div class="flex gap-2">
                <input 
                  type="text" 
                  value={apiInfo.authkey}
                  readonly
                  class="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                />
                <button 
                  on:click={() => apiInfo && copyToClipboard(apiInfo.authkey, 'AuthKey')}
                  class="px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors text-xs font-medium"
                  title="Copiar AuthKey"
                >
                  📋
                </button>
              </div>
              <p class="text-xs text-indigo-300/60">
                Token de autenticación para la API externa
              </p>
            </div>
            
            <!-- Ejemplo de uso -->
            <div class="bg-black/40 border border-white/10 rounded-xl p-3">
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Ejemplo de Request</label>
              <pre class="text-xs text-indigo-200/80 font-mono overflow-x-auto bg-black/50 p-2 rounded-lg border border-white/5">{`POST /api/v1/api/sendText
Authorization: Bearer ${apiInfo.authkey}

{
  "to": "51972053723",
  "message": "Hola mundo",
  "appkey": "${apiInfo.appkey}",
  "authkey": "${apiInfo.authkey}"
}`}</pre>
            </div>
          </div>
          
          <!-- Columna derecha: QR Code -->
          <div class="w-80 flex-shrink-0 border-l border-white/10 pl-6 flex flex-col">
            <div class="flex items-center justify-between mb-3">
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider">Código QR</label>
              {#if qrStatus}
                {@const statusColor = qrStatus.active ? (qrStatus.requests_remaining > 2 && qrStatus.seconds_remaining > 180 ? 'emerald' : 'yellow') : 'red'}
                {@const statusText = qrStatus.active ? (qrStatus.requests_remaining > 2 && qrStatus.seconds_remaining > 180 ? 'Activo' : 'Casi expirado') : 'Expirado'}
                <span class="px-2 py-1 rounded-full text-xs font-bold 
                  {statusColor === 'emerald' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : ''}
                  {statusColor === 'yellow' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' : ''}
                  {statusColor === 'red' ? 'bg-red-500/20 text-red-300 border border-red-500/30' : ''}
                ">
                  {qrStatus.active ? '●' : '○'} {statusText}
                </span>
              {/if}
            </div>
            
            {#if !apiQRImage}
              <!-- Botón para generar QR -->
              <div class="flex flex-col items-center justify-center gap-4 flex-1 py-8">
                <p class="text-sm text-indigo-300/70 text-center">
                  Genera un código QR seguro para compartir los datos de configuración
                </p>
                <button 
                  on:click={generateAPIQR}
                  disabled={generatingTempUrl}
                  class="px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 hover:from-purple-400 hover:via-pink-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title="Generar QR seguro"
                >
                  {generatingTempUrl ? 'Generando...' : '📱 Generar QR'}
                </button>
                <p class="text-xs text-indigo-300/50 text-center">
                  El QR contiene una URL temporal que expira en 5 requests o 5 minutos
                </p>
              </div>
            {:else}
              <!-- QR generado -->
              <div class="flex flex-col items-center gap-3 flex-1">
                <div class="bg-white p-3 rounded-xl">
                  <img 
                    src={apiQRImage} 
                    alt="API QR Code" 
                    class="w-full max-w-[240px] h-auto"
                  />
                </div>
                
                <!-- Estado del QR -->
                {#if qrStatus}
                  <div class="w-full bg-black/40 border border-white/10 rounded-lg p-3 space-y-2">
                    <div class="flex items-center justify-between text-xs">
                      <span class="text-indigo-300/70">Requests:</span>
                      <span class="font-bold text-white">
                        {qrStatus.requests_used}/{qrStatus.max_requests}
                        {#if qrStatus.requests_remaining > 0}
                          <span class="text-emerald-300">({qrStatus.requests_remaining} restantes)</span>
                        {:else}
                          <span class="text-red-300">(agotados)</span>
                        {/if}
                      </span>
                    </div>
                    <div class="flex items-center justify-between text-xs">
                      <span class="text-indigo-300/70">Tiempo restante:</span>
                      <span class="font-bold {qrStatus.seconds_remaining > 180 ? 'text-emerald-300' : qrStatus.seconds_remaining > 60 ? 'text-yellow-300' : 'text-red-300'}">
                        {qrStatus.seconds_remaining > 0 ? `${Math.floor(qrStatus.seconds_remaining / 60)}:${String(qrStatus.seconds_remaining % 60).padStart(2, '0')}` : 'Expirado'}
                      </span>
                    </div>
                  </div>
                {:else if checkingStatus}
                  <p class="text-xs text-indigo-300/60 animate-pulse">Verificando estado...</p>
                {/if}
                
                <p class="text-xs text-indigo-300/70 text-center">
                  Escanea con tu app para obtener los datos
                </p>
                
                <!-- Botones de acción -->
                <div class="flex flex-col gap-2 w-full">
                  <button 
                    on:click={downloadQR}
                    class="w-full px-3 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-2"
                    title="Descargar QR"
                  >
                    ⬇️ Descargar
                  </button>
                  <button 
                    on:click={() => {
                      navigator.clipboard.writeText(apiQRImage)
                      addToast('QR copiado al portapapeles (como imagen)', 'success')
                    }}
                    class="w-full px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-2"
                    title="Copiar QR"
                  >
                    📋 Copiar QR
                  </button>
                  <button 
                    on:click={regenerateQR}
                    disabled={generatingTempUrl}
                    class="w-full px-3 py-2 bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Regenerar QR (revoca el anterior)"
                  >
                    🔄 {generatingTempUrl ? 'Regenerando...' : 'Regenerar QR'}
                  </button>
                </div>

                <!-- URL temporal -->
                {#if apiQRTempUrl}
                  <div class="w-full space-y-1.5 mt-2">
                    <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider">URL Temporal</label>
                    <div class="flex gap-2">
                      <input 
                        type="text" 
                        value={apiQRTempUrl}
                        readonly
                        class="flex-1 px-2 py-1.5 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                      />
                      <button 
                        on:click={copyTempQRUrl}
                        class="px-2 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 rounded-lg transition-colors text-xs"
                        title="Copiar URL"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      {:else}
        <div class="text-center py-12">
          <p class="text-indigo-200/60">No se pudo obtener la información de API.</p>
        </div>
      {/if}
    </div>
  </div>
{/if}
